import React from 'react';

import { Box, Stack, Typography } from '@mui/material';

import { OfferCategory, OfferRequirements } from '../../../../graphql/API';
import { RadioContentBuy } from '../../../../pages/CreateUpdateOffer/components/formiks/OfferRequirement/RadioContentBuy';
import { TikTokOfferRequirement } from '../../../../pages/OfferDetail/components/TikTokOfferRequirement';
import { useAppSelector } from '../../../../redux/hooks';
import Icons from '../../../../themes/icons';
import { centsToDollars } from '../../../../utils/formatter';
import Button from '../../../Button/Button';
import { InvitedUsers } from '../../../Dialogs/CreateInvite/components/InvitedUsers';
import { useCreateInvite } from '../../../Dialogs/CreateInvite/context/CreateInvite.context';
import { CreateInviteProviderProps } from '../../../Dialogs/CreateInvite/context/CreateInvite.provider';
import { ExtendedOfferRequirements } from '../../../Dialogs/CreateInvite/context/types';
import SvgIcon, { IconName } from '../../../Icons';

const requirements: Record<string, string>[] = [
  {
    name: '1 Story',
    value: OfferRequirements.postOnInstagramStories,
    iconName: 'StoryBlack',
    checkedIconName: 'Story',
  },
  {
    name: '3 Stories',
    value: OfferRequirements.postOnInstagramStories3x,
    iconName: 'StoryBlack',
    checkedIconName: 'Story',
  },
  {
    name: 'Feed',
    value: OfferRequirements.postOnInstagramFeed,
    iconName: 'FeedBlack',
    checkedIconName: 'Feed',
  },
  {
    name: 'Reel',
    value: OfferRequirements.postOnInstagramReels,
    iconName: 'ReelBlack',
    checkedIconName: 'Reel',
  },
];

type Props = {
  handleUserDeletion?: CreateInviteProviderProps['handleUserDeletion'];
};

export const Step3: React.FC<Props> = ({ handleUserDeletion }) => {
  const { state, dispatch } = useCreateInvite();
  const balance = useAppSelector((state) => state.balance.value);

  const [usersWithOfferPrice, setUsersWithOfferPrice] = React.useState<
    Record<number, number | undefined>
  >({});

  const totalBalance = React.useMemo(
    () => centsToDollars(balance?.balance || 0),
    [balance?.balance],
  );

  const handleUsersWithOfferPrice = (usersWithOfferPrice: {
    userID: number;
    offerPrice: number | undefined;
  }) => {
    setUsersWithOfferPrice((prev) => ({
      ...prev,
      [usersWithOfferPrice.userID]: usersWithOfferPrice.offerPrice,
    }));
  };

  React.useEffect(() => {
    dispatch({
      type: 'update',
      payload: {
        usersWithOfferPrice: Object.keys(usersWithOfferPrice).map((userId) => ({
          userID: Number(userId),
          offerPrice: usersWithOfferPrice[Number(userId)] || 0,
        })),
        previousTotalBalance: totalBalance,
      },
    });
  }, [dispatch, totalBalance, usersWithOfferPrice]);

  React.useEffect(() => {
    if (
      state?.offer?.category === OfferCategory.event ||
      (state?.offer?.category === OfferCategory.product &&
        state.requirements === ExtendedOfferRequirements.noRequirement)
    ) {
      setUsersWithOfferPrice(
        state.users.reduce((acc, key) => {
          acc = {
            ...acc,
            [key.id]: 0,
          };
          return acc;
        }, {}),
      );
    }
  }, [state?.offer, state.requirements, state.users]);

  const inviteRequirements = React.useMemo(
    () => [
      ...requirements,
      ...(state.offer?.category === OfferCategory.event ||
      state.offer?.category === OfferCategory.product
        ? [
            {
              name: 'No Req.',
              value: ExtendedOfferRequirements.noRequirement,
              iconName: 'NoRequirement',
              checkedIconName: 'NoRequirement',
            },
          ]
        : []),
    ],
    [state.offer],
  );

  const handleOfferPricesValidatity = (val: boolean) => {
    dispatch({ type: 'update', payload: { offerPricesValid: val } });
  };

  const handlerUserDeletion = (userID: number) => {
    dispatch({
      type: 'update',
      payload: {
        users: state.users.filter((user) => Number(user.id) !== userID),
      },
    });
    setUsersWithOfferPrice((prev) => {
      const newObj = { ...prev };
      delete newObj[userID];
      return newObj;
    });

    if (handleUserDeletion) handleUserDeletion(userID);
  };

  const onBack = () => {
    dispatch({ type: 'prev' });
  };

  return (
    <Box>
      <Box
        display={'flex'}
        alignItems={'center'}
        justifyContent={'space-between'}
        sx={{ borderBottom: '1px solid #F1F2F1', backgroundColor: '#FCFCFC', px: 5, py: 3 }}
      >
        <Box display={'flex'} alignItems={'center'} gap={1.5}>
          <Button startIcon={<Icons.BackIcon />} onClick={onBack} />
          <Typography fontWeight={'500'} sx={{ fontFamily: 'Poppins', fontSize: '18px' }}>
            Checkout
          </Typography>
        </Box>
      </Box>
      <Stack spacing={4}>
        <Box sx={{ px: 5, py: 2, display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <Box display={'flex'} alignItems={'center'} gap={2}>
            {inviteRequirements
              .filter((requirement) => requirement.value === state.requirements)
              .map((requirement) => {
                return (
                  <Box minWidth={'200px'}>
                    <RadioContentBuy
                      checked={state.requirements === requirement.value}
                      label={requirement.name}
                      icon={<SvgIcon name={requirement.iconName as IconName} />}
                      checkedIcon={<SvgIcon name={requirement.checkedIconName as IconName} />}
                      onClick={() => {
                        dispatch({
                          type: 'update',
                          payload: { requirements: requirement.value ?? undefined },
                        });
                      }}
                    />
                  </Box>
                );
              })}
          </Box>
          {state.requirements === OfferRequirements.postOnTikTokVideo ? (
            <Box minWidth={350} maxWidth={350}>
              <TikTokOfferRequirement tag={state.tag} />
            </Box>
          ) : null}
        </Box>
        <Box sx={{ height: 'calc(100vh - 360px)', overflow: 'auto' }}>
          <InvitedUsers
            offer={state.offer}
            users={state.users}
            usersWithOfferPrice={usersWithOfferPrice}
            handleUsersWithOfferPrice={handleUsersWithOfferPrice}
            handleOfferPricesValidatity={handleOfferPricesValidatity}
            requirements={state.requirements}
            handleUserDelete={handlerUserDeletion}
          />
        </Box>
      </Stack>
    </Box>
  );
};
